import React, { useEffect, useState } from 'react';
import { Link, NavLink, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Dropdown } from 'react-bootstrap';
import FeatherIcon from 'feather-icons-react';
import { Logout_Url } from '../../api';
import logo from '../../assets/images/equip-logo.png';
import { deleteUser } from '../../store/userSlice';
import { ADMIN_PATH, SCHOOL_PATH, USER_ROLE } from '../../config/Constants';
import { httpGet, removeHttpToken } from '../../utilities/http';
import { removeToken } from '../../utilities/utils';
import SupportModal from '../SupportModal';
import Alert from '../Alert';
import FullScreenLoader from './FullScreenLoader';
import CopyReferralLinkButton from './CopyReferralLinkButton';
import { sendAmplitudeData } from '../../utilities/amplitude';
import Notification from './Notification';

export default function Header() {
  const user = useSelector((state) => state.user);
  const alert = useSelector((state) => state.general.alert);
  const roleId = user?.role?.id;
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [showSupportModal, setShowSupportModal] = useState(false);
  const [isSigningOut, setIsSigningOut] = useState(false);

  useEffect(() => {
    if (isMenuOpen) {
      document.body.classList.add('navbar-nav-show');
    } else {
      document.body.classList.remove('navbar-nav-show');
    }
  }, [isMenuOpen]);

  useEffect(() => {
    if (showSupportModal) {
      setIsMenuOpen(false);
    }
  }, [showSupportModal]);

  const getImageName = (name) => {
    const names = name.split(' ');
    if (names.length >= 2) {
      return names[0].charAt(0) + names[1].charAt(0);
    }
    return names[0].charAt(0);
  };

  const logout = () => {
    setIsSigningOut(true);
    httpGet(Logout_Url)
      .then(() => {
        removeToken();
        dispatch(deleteUser());
      })
      .finally(() => {
        removeToken();
        removeHttpToken();
        dispatch(deleteUser());
        navigate('/');
        setIsSigningOut(false);
      });
  };

  return (
    <>
      <div
        className="backdrop"
        onClick={() => {
          setIsMenuOpen(false);
        }}
      />

      <Alert visible={alert.visible} type={alert.type} messages={alert.messages} />

      {isSigningOut && <FullScreenLoader isTransparent />}

      <SupportModal
        show={showSupportModal}
        onClose={() => {
          setShowSupportModal(false);
        }}
      />

      <header
        className="navbar navbar-header navbar-header-fixed justify-content-between align-items-center pd-x-20 tl-top-0"
        id="tl-header"
      >
        <Notification />
        <div className="d-flex align-items-center tl-col-gap-15">
          {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
          {roleId && (
            <a
              id="mainMenuOpen"
              className="burger-menu"
              onClick={() => {
                setIsMenuOpen(true);
              }}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="feather feather-menu"
              >
                <line x1="3" y1="12" x2="21" y2="12" />
                <line x1="3" y1="6" x2="21" y2="6" />
                <line x1="3" y1="18" x2="21" y2="18" />
              </svg>
            </a>
          )}
          <Logo roleId={roleId} />
        </div>
        {roleId && (
          <>
            <MobileMenu roleId={roleId} setIsMenuOpen={setIsMenuOpen} setShowSupportModal={setShowSupportModal} />
          </>
        )}
        {roleId === USER_ROLE.SCHOOL_ADMIN && (
          <div className="tl-col-gap-20 align-items-center tx-roboto tx-15 d-none d-lg-flex">
            <CustomLink link="dashboard" label="Dashboard" parent={SCHOOL_PATH} />
            <CustomLink link="staff" label="Staff Details" parent={SCHOOL_PATH} />
          </div>
        )}
        {roleId === USER_ROLE.ADMIN && (
          <div className="tl-col-gap-20 align-items-center tx-roboto tx-15 d-none d-lg-flex">
            <CustomLink link="dashboard" label="Dashboard" parent={ADMIN_PATH} />
            <CustomLink link="schools" label="Schools" parent={ADMIN_PATH} />
            <CustomLink link="resource" label="Resources" parent={ADMIN_PATH} />
            <CustomLink link="users" label="User List" parent={ADMIN_PATH} />
          </div>
        )}
        <div className="d-flex align-items-center tl-col-gap-30">
          {(roleId === USER_ROLE.SCHOOL_ADMIN ||
            roleId === USER_ROLE.SCHOOL_STAFF ||
            roleId === USER_ROLE.DEMO_USER) && (
            <div className="d-none d-md-block">
              <SupportButton setShowSupportModal={setShowSupportModal} />
            </div>
          )}

          {roleId === USER_ROLE.SCHOOL_ADMIN && window.location.pathname.includes('school') && (
            <div className="d-none d-lg-block">
              <CopyReferralLinkButton />
            </div>
          )}

          {roleId && (
            <Dropdown>
              <Dropdown.Toggle className="tl-avatar-toggle" id="dropdown-basic" as="span" drop="none">
                <span className="cursor-pointer">
                  <div className="avatar avatar-sm">
                    <span className="avatar-initial rounded-circle">{getImageName(user.name)}</span>
                  </div>
                </span>
              </Dropdown.Toggle>

              <Dropdown.Menu align="right" style={{ marginTop: '12px', marginRight: '-10px' }}>
                <div className="tx-13 pd-25 tl-wd-230">
                  <div className="avatar avatar-lg mg-b-15">
                    <span className="avatar-initial rounded-circle">{getImageName(user.name)}</span>
                  </div>
                  <h6 className="tx-semibold mg-b-5">{user.name}</h6>
                  <p className="mg-b-25 tx-12 tx-color-03">{user.email}</p>

                  <div className="d-flex flex-column row-gap-10">
                    {roleId === USER_ROLE.SCHOOL_ADMIN && window.location.pathname.includes('school') && (
                      <span
                        onClick={() => {
                          setShowSupportModal(true);
                          sendAmplitudeData(`Support`, {});
                        }}
                        className="cursor-pointer"
                      >
                        <FeatherIcon icon="help-circle" className="wd-15 ht-15 mg-r-15" />
                        Support
                      </span>
                    )}
                    {((roleId === USER_ROLE.SCHOOL_ADMIN && !window.location.pathname.includes('school')) ||
                      roleId === USER_ROLE.SCHOOL_STAFF) && (
                      <div
                        onClick={() => {
                          navigate('/certificate');
                        }}
                        className="cursor-pointer"
                      >
                        <FeatherIcon icon="paperclip" className="wd-15 ht-15 mg-r-15" />
                        My Certificate
                      </div>
                    )}
                    {!(roleId === USER_ROLE.SCHOOL_ADMIN || roleId === USER_ROLE.SCHOOL_STAFF) && (
                      <div className="dropdown-divider" />
                    )}
                    <span
                      onClick={() => {
                        logout();
                      }}
                      className="cursor-pointer"
                    >
                      <FeatherIcon icon="log-out" className="wd-15 ht-15 mg-r-15" />
                      Sign Out
                    </span>
                  </div>
                </div>
              </Dropdown.Menu>
            </Dropdown>
          )}
        </div>
      </header>
    </>
  );
}

const SupportButton = ({ setShowSupportModal }) =>
  !window.location.pathname.includes('school') ? (
    <span
      onClick={() => {
        setShowSupportModal(true);
        sendAmplitudeData(`Support`, {});
      }}
      style={{ cursor: 'pointer' }}
    >
      Support
    </span>
  ) : null;

const MobileMenu = ({ roleId, setIsMenuOpen, setShowSupportModal }) => (
  <div className="navbar-menu-wrapper d-block d-lg-none" id="navbarMenu">
    <div className="navbar-menu-header mg-t-15 pd-b-15">
      <Logo roleId={roleId} />
      <FeatherIcon
        icon="x"
        onClick={() => {
          setIsMenuOpen(false);
        }}
      />
    </div>
    <ul className="nav navbar-menu">
      <li className="nav-label pd-l-20 pd-lg-l-25 d-lg-none">Main Navigation</li>
      {roleId === USER_ROLE.ADMIN && (
        <>
          <li
            className="nav-item"
            onClick={() => {
              setIsMenuOpen(false);
            }}
          >
            <CustomLink link="dashboard" label="Dashboard" parent={ADMIN_PATH} />
          </li>
          <li
            className="nav-item"
            onClick={() => {
              setIsMenuOpen(false);
            }}
          >
            <CustomLink link="resource" label="Resources" parent={ADMIN_PATH} />
          </li>
          <li
            className="nav-item"
            onClick={() => {
              setIsMenuOpen(false);
            }}
          >
            <CustomLink link="users" label="Users List" parent={ADMIN_PATH} />
          </li>
        </>
      )}
      {roleId === USER_ROLE.SCHOOL_ADMIN && (
        <>
          <li
            className="nav-item"
            onClick={() => {
              setIsMenuOpen(false);
            }}
          >
            <CustomLink link="dashboard" label="Dashboard" parent={SCHOOL_PATH} />
          </li>
          <li
            className="nav-item"
            onClick={() => {
              setIsMenuOpen(false);
            }}
          >
            <CustomLink link="staff" label="Staff Details" parent={SCHOOL_PATH} />
          </li>
        </>
      )}
      <li className="nav-item">
        <div className="nav-link">
          <SupportButton setShowSupportModal={setShowSupportModal} />
        </div>
      </li>
    </ul>
  </div>
);

const Logo = ({ roleId }) => (
  <Link
    to={
      roleId === USER_ROLE.ADMIN
        ? '/admin/dashboard'
        : roleId === USER_ROLE.DEMO_USER || roleId === USER_ROLE.SCHOOL_ADMIN || roleId === USER_ROLE.SCHOOL_STAFF
        ? '/dashboard'
        : '#'
    }
    className="df-logo"
  >
    <img src={logo} alt="" className="logo-h" />
  </Link>
);

const CustomLink = ({ link, label, parent }) => (
  <NavLink
    to={parent ? `${parent}/${link}` : `/${link}`}
    className={({ isActive }) => {
      const className = ['nav-link'];
      if (isActive) {
        className.push('tl-active-link');
      } else {
        className.push('tl-color-4e4e50');
      }
      return className.join(' ');
    }}
  >
    {label}
  </NavLink>
);
